/**
 * @file PickQuantityDialog.tsx
 * @description Ручной ввод количества по строке задания (phase19.14).
 *
 * Отдельный fullScreen-диалог на <sm — решение экрана B шага 0: на 360px
 * инлайн-инпут закрывается клавиатурой, и кладовщик не видит, что вводит.
 */

import { useState } from "react";

import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import {
  formatPickItemName,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import {
  formatPlannedQty,
  formatQtyValue,
} from "../../../lib/picking-format";
import { qtyInputProps, validateQty } from "../../../lib/qty-input";

interface Props {
  item: PickingTaskItemDto;
  saving: boolean;
  error: string | null;
  onSubmit: (qty: number) => void;
  onClose: () => void;
}

export function PickQuantityDialog({
  item,
  saving,
  error,
  onSubmit,
  onClose,
}: Props) {
  const planned = Number(item.qtyPlanned);
  // Шаг стрелок и клавиатура — по единице строки: фасовки («шт») крутятся по
  // одной, тысячные доли пачки кладовщик собрать не может.
  const qtyProps = qtyInputProps(item.unit);
  // Сервер отдаёт Decimal строкой («3.000»): в поле подставляем человеческий
  // вид, иначе кладовщик правит «0.000» и читает его как тысячи.
  const [value, setValue] = useState(() => formatQtyValue(item.qtyPicked));
  const [localError, setLocalError] = useState<string | null>(null);

  function submit() {
    const message = validateQty(value, item.unit, { min: 0, max: planned });
    if (message) {
      setLocalError(message);
      return;
    }
    setLocalError(null);
    onSubmit(Number(value.replace(",", ".")));
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Сколько собрано</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="text-sm">
            <div className="font-medium">{formatPickItemName(item)}</div>
            <div className="text-on-surface-variant">
              По плану: {formatPlannedQty(item.qtyPlanned, item.unit)}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pick-qty">Количество</Label>
            <input
              id="pick-qty"
              type="number"
              inputMode={qtyProps.inputMode}
              step={qtyProps.step}
              min={0}
              max={planned}
              value={value}
              autoFocus
              onChange={(e) => {
                setValue(e.target.value);
                setLocalError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
              disabled={saving}
              className="min-h-[56px] rounded-xl border border-on-surface/15 bg-surface-lowest px-3 text-xl tabular-nums"
            />
          </div>
          {localError ?? error ? (
            <Alert severity="error">{localError ?? error}</Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="min-h-12" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button className="min-h-12" onClick={submit} disabled={saving}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
